import { readonly, ref } from 'vue'

import { useTimeoutPoll } from '@vueuse/core'

import { APP_VERSION_POLL_TIME } from '~/portal/utils'

const PROMPTED_VERSION_KEY = 'app-version-prompted'

const getBootScripts = (): string[] =>
	Array.from(
		document.querySelectorAll<HTMLScriptElement>(
			'script[type="module"][src]'
		)
	)
		.map((s: HTMLScriptElement): string => s.getAttribute('src') ?? '')
		.filter(Boolean)

const getServedScripts = (html: string): string[] =>
	Array.from(
		html.matchAll(/<script[^>]*type="module"[^>]*src="([^"]+)"/g),
		(match: RegExpMatchArray): string => match[1]
	)

const bootScripts: string[] = getBootScripts()

const pendingVersion = ref<string | null>(null)

const getPromptedVersion = (): string | null => {
	try {
		return localStorage.getItem(PROMPTED_VERSION_KEY)
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('[AppVersion] Failed to read local storage', error)
		return null
	}
}

const setPromptedVersion = (version: string): void => {
	try {
		localStorage.setItem(PROMPTED_VERSION_KEY, version)
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('[AppVersion] Failed to write local storage', error)
	}
}

const checkForUpdate = async (): Promise<void> => {
	if (!bootScripts.length) {
		return
	}

	let html: string
	try {
		const response: Response = await fetch(`/?_=${Date.now()}`, {
			cache: 'no-store'
		})

		if (!response.ok) {
			return
		}

		html = await response.text()
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('[AppVersion] Version check request failed', error)
		return
	}

	const isStale: boolean = bootScripts.some(
		(src: string): boolean => !html.includes(src)
	)

	if (!isStale) {
		return
	}

	const versionKey: string = getServedScripts(html).join('|') || 'unknown'

	if (getPromptedVersion() === versionKey) {
		return
	}

	setPromptedVersion(versionKey)
	pendingVersion.value = versionKey
}

export const useAppVersion = () => {
	const { resume: startVersionCheck, pause: stopVersionCheck } =
		useTimeoutPoll(checkForUpdate, APP_VERSION_POLL_TIME)

	const reloadApp = (): void => {
		window.location.reload()
	}

	return {
		pendingVersion: readonly(pendingVersion),
		startVersionCheck,
		stopVersionCheck,
		checkForUpdate,
		reloadApp
	}
}
